/**
 * userPrefs — per-user local storage for signed-in preferences.
 *
 * localStorage is scoped to the ORIGIN, not to the signed-in account. Preferences
 * were stored under bare keys (`canira_studio_pinned_tabs`, `sidebarSections`,
 * `canira_mic_settings`, …), so on any browser where more than one person signs
 * in — a shared studio machine, an admin checking a host's account, a demo
 * laptop — each account read and overwrote the previous one's setup. The
 * reported symptom was one account's pinned Command Center tiles appearing for
 * everyone else; the same bleed covered microphone settings, saved scenes,
 * dashboard layout, and the onboarding flags that decide whether somebody is
 * shown a first-run tour.
 *
 * ONLY for surfaces behind authentication. Reads and writes are no-ops without a
 * signed-in user, so a key belonging to a PUBLIC surface — a viewer's display
 * name on a watch page, an evergreen attendee's name, the theme hint every
 * first-time visitor sees — must NOT be routed through here, or it silently
 * stops persisting for the anonymous visitors it exists for. Auth tokens are
 * likewise out of scope: they are how this module knows who the user is.
 *
 * Keys are suffixed with the signed-in user's id, and reads NEVER fall back to
 * the unscoped key — falling back is the leak. A session with no identifiable
 * user reads nothing and writes nothing, so it gets defaults rather than
 * somebody else's setup.
 *
 * The user id comes from the cached user object rather than from React context
 * because these are consumed by useState initializers on the studio's FIRST
 * render, where AuthContext's currentUser is still null (it hydrates async).
 * `canira_user` is written at login and on every profile refresh, and cleared on
 * logout, so it is present and correct for exactly as long as a session is.
 *
 * `canira_studio_dest_prefs` already did this — keyed by user id inside its own
 * blob — and is left alone. This generalises that idea to everything else.
 */

const USER_KEY = 'canira_user';

/**
 * Signed-in user's id, or null. Never throws: storage can be unavailable
 * (private mode, disabled cookies) and a dead meter is better than a dead page.
 */
export function currentPrefsUserId() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const id = JSON.parse(raw)?.id;
    return id === undefined || id === null || id === '' ? null : String(id);
  } catch {
    return null;
  }
}

/**
 * Storage key for a preference, scoped to a user. Returns null without a user —
 * callers treat that as "no stored value", which is what keeps an unidentified
 * session from reading or writing a shared key.
 */
export function scopedPrefKey(baseKey, userId) {
  if (!baseKey || !userId) return null;
  return `${baseKey}::u${userId}`;
}

/**
 * Read a preference for the signed-in user.
 * @returns {string|null} raw stored string, or null if absent / no user.
 */
export function readPref(baseKey, userId = currentPrefsUserId()) {
  const key = scopedPrefKey(baseKey, userId);
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Write a preference for the signed-in user. No-op without a user, so a
 * half-initialised session cannot persist defaults over somebody's real setup.
 * @returns {boolean} whether it was stored
 */
export function writePref(baseKey, value, userId = currentPrefsUserId()) {
  const key = scopedPrefKey(baseKey, userId);
  if (!key) return false;
  try {
    localStorage.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
}

/**
 * Delete a preference for the signed-in user. No-op without one, so a
 * signed-out tab cannot clear somebody else's saved draft.
 * @returns {boolean} whether it was removed
 */
export function removePref(baseKey, userId = currentPrefsUserId()) {
  const key = scopedPrefKey(baseKey, userId);
  if (!key) return false;
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Every key that used to be stored unscoped and is now per-user.
 *
 * Deliberately does NOT list keys on public surfaces (`canira:viewer:name`,
 * `canira:viewer:chatCollapsed`, `evergreen_user_name`,
 * `canira_replay_cta_dismissed`, `canira-theme-toast-shown`) — those belong to
 * anonymous visitors, have no user to scope by, and deleting them would log
 * people out of their own display name. Nor auth keys, for obvious reasons.
 */
export const LEGACY_UNSCOPED_KEYS = [
  // Broadcast Studio
  'canira_studio_pinned_tabs',
  'canira_studio_collapsed_groups',
  'canira_studio_visited',
  'canira_mic_settings',
  'canira_camera_adjustments',
  'canira_chat_sections_collapsed',
  'canira_overlay_frames_collapsed',
  'canira_caption_language',
  'canira_sidebar_expanded_once',
  'canira_last_sidebar_tab',
  'broadcastStudio.sidebarCollapsed',
  'canira_custom_scenes',
  // Dashboard shell
  'sidebarCollapsed',
  'sidebarSections',
  'quickActionsBannerCollapsed',
  // Editors and tools. The clip draft is the host's own work-in-progress, not
  // UI state, which makes it the most consequential entry in this list.
  'canira.aiClipDraft.v1',
  'webinarEditCollapsed',
  'canira_pb_left_rail_collapsed',
  'canira_pb_right_rail_collapsed',
  'canira.thumbnailGen.model',
  'canira.clipSortMode',
  'aiThumbGalleryCollapsed',
  // Device selection (behind /setup, which is a PrivateRoute)
  'preferredVideoDevice',
  'preferredAudioInput',
  'preferredAudioOutput',
];

/**
 * Delete the pre-scoping copies of these preferences.
 *
 * Deliberately a delete and NOT a migration. There is no way to tell which
 * account wrote an unscoped value, so adopting it would hand the next person to
 * sign in whatever the last person left — the exact bug being fixed. Everyone
 * re-sets their preferences once; nobody inherits a stranger's.
 */
export function purgeLegacyPrefs(baseKeys = LEGACY_UNSCOPED_KEYS) {
  baseKeys.forEach((baseKey) => {
    try {
      localStorage.removeItem(baseKey);
    } catch {
      /* storage unavailable — nothing to purge */
    }
  });
}
